(() => {
  if (window.racloakV1ProductCard) return;

  const cardSelector = '[data-racloak-v1-product-card]';
  const colorSelector = '[data-racloak-v1-product-card-color]';
  const hoverImageSelector = '.racloak-v1-product-card__image--hover';
  const badgeClassPrefix = 'racloak-v1-product-card__badge--';

  // One owner for row geometry, including filtered and asynchronously added cards.
  let rowFrame = 0;
  const containers = new Map();
  const scheduleRows = () => {
    if (!rowFrame) rowFrame = requestAnimationFrame(alignRows);
  };
  const resizeRows = new ResizeObserver((entries) => {
    entries.forEach(({ target, contentRect }) => {
      if (containers.get(target) !== contentRect.width) {
        containers.set(target, contentRect.width);
        scheduleRows();
      }
    });
  });
  const visibleRows = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) scheduleRows();
  });
  const alignRows = () => {
    rowFrame = 0;
    const groups = new Map();
    const cards = Array.from(document.querySelectorAll(cardSelector));
    cards.forEach((card) => {
      card.style.removeProperty('--racloak-card-row-title');
      card.style.removeProperty('--racloak-card-row-copy');
      card.style.removeProperty('--racloak-v1-product-card-price-shift');
      card.style.removeProperty('--racloak-v1-product-card-swatches-transform');
      card.style.removeProperty('--racloak-v1-product-card-reviews-transform');
      const parent = card.parentElement;
      if (!groups.has(parent)) groups.set(parent, []);
      groups.get(parent).push(card);
    });
    containers.forEach((_, parent) => {
      if (!groups.has(parent)) {
        resizeRows.unobserve(parent);
        visibleRows.unobserve(parent);
        containers.delete(parent);
      }
    });
    const updates = [];
    groups.forEach((members, parent) => {
      if (!containers.has(parent)) {
        containers.set(parent, -1);
        resizeRows.observe(parent);
        visibleRows.observe(parent);
      }
      const rows = [];
      members.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const copy = card.querySelector('.racloak-v1-product-card__copy');
        const title = card.querySelector('.racloak-v1-product-card__title');
        if (!rect.width || !rect.height || !copy || !title) return;
        let row = rows.find((item) => Math.abs(item.top - rect.top) < 2);
        if (!row) rows.push(row = { top: rect.top, items: [] });
        const subtitle = card.querySelector('.racloak-v1-product-card__subtitle');
        row.items.push({ card, title: title.getBoundingClientRect().height,
          subtitle: subtitle?.getBoundingClientRect().height || 0,
          gap: parseFloat(getComputedStyle(copy).rowGap) || 0 });
      });
      rows.forEach(({ items }) => {
        const title = Math.max(...items.map((item) => item.title));
        const subtitle = Math.max(...items.map((item) => item.subtitle));
        const gap = subtitle ? Math.max(...items.map((item) => item.gap)) : 0;
        items.forEach(({ card }) => updates.push({ card, title, copy: title + gap + subtitle }));
      });
    });
    updates.forEach(({ card, title, copy }) => {
      card.style.setProperty('--racloak-card-row-title', `${title}px`);
      card.style.setProperty('--racloak-card-row-copy', `${copy}px`);
    });
    // Single-product collection cards: preserve row tracks and only shift details when needed.
    updates.forEach(({ card }) => {
      if (!card.matches('[data-racloak-v1-product-collection] .racloak-v1-product-card:not(.racloak-v1-product-card--compact)[data-racloak-v1-wishlist-item^="product--"]')) return;
      const title = card.querySelector('.racloak-v1-product-card__title');
      const price = card.querySelector('.racloak-v1-product-card__price');
      const copy = card.querySelector('.racloak-v1-product-card__copy');
      if (!title || !price || !copy) return;
      const gap = parseFloat(getComputedStyle(copy).rowGap) || 8;
      const titleBox = title.getBoundingClientRect();
      const titleLink = card.querySelector('[data-racloak-v1-product-card-title-link]');
      const textBox = titleLink?.getBoundingClientRect();
      const visibleTextBottom = textBox?.height ? Math.min(textBox.bottom, titleBox.bottom) : titleBox.bottom;
      const priceBox = price.getBoundingClientRect();
      const shift = visibleTextBottom + gap - priceBox.top;
      card.style.setProperty('--racloak-v1-product-card-price-shift', shift + 'px');
      let detailsBottom = visibleTextBottom + gap + priceBox.height;
      ['swatches', 'reviews'].forEach((part) => {
        const detail = card.querySelector('.racloak-v1-product-card__' + part);
        const box = detail?.getBoundingClientRect();
        if (!box?.height) return;
        const margin = parseFloat(getComputedStyle(detail).marginTop) || 16;
        const offset = Math.max(0, detailsBottom + margin - box.top);
        if (offset) card.style.setProperty('--racloak-v1-product-card-' + part + '-transform', 'translateY(' + offset + 'px)');
        detailsBottom = box.bottom + offset;
      });
    });
  };
  new MutationObserver((records) => {
    if (records.some(({ type, target, addedNodes, removedNodes }) => {
      const element = target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement;
      if (element?.closest(cardSelector)) return true;
      if (type !== 'childList') return Boolean(element?.querySelector(cardSelector));
      const nodes = [...addedNodes, ...removedNodes];
      return containers.has(element) || nodes.some((node) =>
        node.matches?.(cardSelector) || node.querySelector?.(cardSelector))
        || nodes.some((node) => node.matches?.('style,link[rel~="stylesheet"]')
          || node.querySelector?.('style,link[rel~="stylesheet"]')) && Boolean(element?.querySelector(cardSelector));
    })) scheduleRows();
  }).observe(document.documentElement, {
    subtree: true, childList: true, characterData: true,
    attributes: true, attributeFilter: ['class', 'hidden'],
  });
  window.addEventListener('resize', scheduleRows);
  document.fonts?.ready.then(scheduleRows);
  document.fonts?.addEventListener('loadingdone', scheduleRows);
  scheduleRows();

  const updateLink = (link, url, label) => {
    if (!link) return;
    link.href = url;
    if (label) link.setAttribute('aria-label', label);
  };

  const hoverLoads = new WeakMap();
  const resetHoverImage = (hover) => {
    hoverLoads.get(hover)?.cleanup();
    hoverLoads.delete(hover);
    delete hover.dataset.racloakV1ProductHoverReady;
  };

  const loadHoverImage = (card) => {
    const hover = card?.querySelector(hoverImageSelector);
    const source = hover?.dataset.racloakV1ProductHoverSrc;
    if (!hover || !source) return;

    const srcset = hover.dataset.racloakV1ProductHoverSrcset || '';
    const sizes = hover.dataset.racloakV1ProductHoverSizes || '';
    const key = [source, srcset, sizes].join('\n');
    const previous = hoverLoads.get(hover);
    if (previous?.key === key && (previous.loading || previous.ready || Date.now() < previous.retryAt)) return;
    resetHoverImage(hover);

    let timer;
    const state = { key, loading: true, ready: false, retryAt: 0, cleanup: () => {
      clearTimeout(timer);
      hover.removeEventListener('load', onLoad);
      hover.removeEventListener('error', onError);
    } };
    const finish = (success) => {
      if (hoverLoads.get(hover) !== state || hover.dataset.racloakV1ProductHoverSrc !== source) return;
      clearTimeout(timer);
      state.loading = false;
      state.ready = success;
      if (success) {
        hover.dataset.racloakV1ProductHoverReady = '';
      } else {
        state.cleanup();
        // Keep the primary visible; a later user activation may retry, without a request storm.
        state.retryAt = Date.now() + 1000;
        delete hover.dataset.racloakV1ProductHoverReady;
        hover.hidden = true;
        hover.removeAttribute('src');
        hover.removeAttribute('srcset');
        hover.removeAttribute('sizes');
      }
    };
    const onLoad = () => finish(hover.complete && hover.naturalWidth > 0);
    const onError = () => finish(false);
    hoverLoads.set(hover, state);
    hover.addEventListener('load', onLoad);
    hover.addEventListener('error', onError);
    timer = setTimeout(onError, 15000);
    hover.hidden = false;
    if (sizes) hover.sizes = sizes;
    if (srcset) hover.srcset = srcset;
    hover.src = source;
  };

  const updateImage = (card, option) => {
    const mediaLink = card.querySelector('.racloak-v1-product-card__media-link');
    const placeholder = card.querySelector('[data-racloak-v1-product-card-placeholder]');
    const primarySource = option.dataset.productImage || '';
    let primary = card.querySelector('.racloak-v1-product-card__image--primary');

    if (!primary && primarySource && mediaLink) {
      primary = document.createElement('img');
      primary.className = 'racloak-v1-product-card__image racloak-v1-product-card__image--primary';
      primary.loading = 'lazy';
      mediaLink.append(primary);
    }

    if (primarySource && primary) {
      primary.hidden = false;
      primary.src = primarySource;
      primary.alt = option.dataset.productImageAlt || option.dataset.productTitle || '';
      if (option.dataset.productImageSrcset) primary.srcset = option.dataset.productImageSrcset;
      else primary.removeAttribute('srcset');
      if (option.dataset.productImageSizes) primary.sizes = option.dataset.productImageSizes;
      else primary.removeAttribute('sizes');
      if (option.dataset.productImageWidth) primary.setAttribute('width', option.dataset.productImageWidth);
      if (option.dataset.productImageHeight) primary.setAttribute('height', option.dataset.productImageHeight);
      if (placeholder) placeholder.hidden = true;
    } else {
      if (primary) {
        primary.hidden = true;
        primary.removeAttribute('src');
        primary.removeAttribute('srcset');
        primary.removeAttribute('sizes');
      }
      if (placeholder) placeholder.hidden = false;
    }

    let hover = card.querySelector('.racloak-v1-product-card__image--hover');
    const hoverSource = option.dataset.productHoverImage || '';
    if (!hover && hoverSource && mediaLink) {
      hover = document.createElement('img');
      hover.className = 'racloak-v1-product-card__image racloak-v1-product-card__image--hover';
      hover.fetchPriority = 'low';
      hover.alt = '';
      mediaLink.append(hover);
    }
    if (hover) {
      resetHoverImage(hover);
      hover.removeAttribute('src');
      hover.removeAttribute('srcset');
      hover.removeAttribute('sizes');
      hover.hidden = !hoverSource;
      if (hoverSource) {
        hover.dataset.racloakV1ProductHoverSrc = hoverSource;
        hover.dataset.racloakV1ProductHoverSrcset = option.dataset.productHoverImageSrcset || '';
        hover.dataset.racloakV1ProductHoverSizes = option.dataset.productHoverImageSizes || '';
        if (option.dataset.productHoverImageWidth) hover.setAttribute('width', option.dataset.productHoverImageWidth);
        if (option.dataset.productHoverImageHeight) hover.setAttribute('height', option.dataset.productHoverImageHeight);
      } else {
        delete hover.dataset.racloakV1ProductHoverSrc;
        delete hover.dataset.racloakV1ProductHoverSrcset;
        delete hover.dataset.racloakV1ProductHoverSizes;
      }
    }
    card.classList.toggle('has-hover-image', Boolean(hoverSource));
    if (hoverSource && mediaLink?.matches?.(':hover, :focus')) loadHoverImage(card);
  };

  const updatePrice = (card, option) => {
    const price = card.querySelector('[data-racloak-v1-product-card-price]');
    if (!price) return;

    const priceText = option.dataset.productPrice || '';
    const priceOnRequest = option.dataset.productPriceOnRequest === 'true';
    const priceVaries = !priceOnRequest && option.dataset.productPriceVaries === 'true';
    const compareAtText = option.dataset.productCompareAtPrice || '';
    const isSale = !priceOnRequest && option.dataset.productOnSale === 'true' && compareAtText !== '';
    const prefix = price.querySelector?.('[data-racloak-v1-product-card-price-prefix]');
    const current = price.querySelector?.('[data-racloak-v1-product-card-price-current]');
    const compare = price.querySelector?.('[data-racloak-v1-product-card-price-compare]');

    if (!current) {
      price.textContent = priceText;
      if (priceVaries) {
        const fallbackPrefix = document.createElement('span');
        fallbackPrefix.className = 'racloak-v1-product-card__price-prefix';
        fallbackPrefix.textContent = 'From:';
        price.prepend(fallbackPrefix);
      }
      return;
    }

    if (prefix) {
      prefix.textContent = 'From:';
      prefix.hidden = !priceVaries;
    }
    current.textContent = priceText;
    if (compare) {
      compare.textContent = isSale ? compareAtText : '';
      compare.hidden = !isSale;
    }
    price.classList.toggle('is-sale', isSale);
  };

  const updateBadge = (card, option) => {
    const badge = card.querySelector('[data-racloak-v1-product-card-badge]');
    if (!badge) return;
    Array.from(badge.classList).forEach((name) => {
      if (name.startsWith(badgeClassPrefix)) badge.classList.remove(name);
    });
    badge.classList.add(`${badgeClassPrefix}${option.dataset.productBadgeKey || 'brand'}`);
    badge.textContent = option.dataset.productBadge || '';
    badge.dataset.racloakV1BadgeType = option.dataset.productBadgeType || 'none';
    badge.dataset.racloakV1BadgeSource = option.dataset.productBadgeSource || 'none';
    badge.hidden = !option.dataset.productBadge;
  };

  const updateReviews = (card, option) => {
    const reviews = card.querySelector('[data-racloak-v1-product-card-reviews]');
    if (!reviews) return;
    const rating = Number(option.dataset.productRating);
    const rounded = Number(option.dataset.productRatingRounded);
    const scale = Number(option.dataset.productRatingScale) || 5;
    const count = option.dataset.productReviewCount || '';
    const hasReviews = Number.isFinite(rating) && rating > 0 && rating <= scale && Number(count) > 0;

    reviews.classList.toggle('is-empty', !hasReviews);
    reviews.setAttribute('aria-hidden', String(!hasReviews));
    if (!hasReviews) return;
    reviews.dataset.racloakV1ReviewSource = option.dataset.productReviewSource || 'shopify';
    const stars = reviews.querySelector('[data-racloak-v1-product-card-stars]');
    stars?.setAttribute('aria-label', `${rating} out of ${scale} stars`);
    stars?.querySelectorAll('[data-racloak-v1-product-card-star]').forEach((star, index) => {
      star.classList.toggle('is-active', index < rounded);
    });
    const countNode = reviews.querySelector('[data-racloak-v1-product-card-review-count]');
    if (countNode) countNode.textContent = `${count} reviews`;
  };

  const updateWishlist = (card, option) => {
    const wishlist = card.querySelector('[data-racloak-v1-wishlist-toggle]');
    if (!wishlist) return;
    const title = option.dataset.productTitle || '';
    const key = option.dataset.productWishlistKey || `product--${option.dataset.productHandle || ''}`;
    card.dataset.racloakV1WishlistItem = key;
    wishlist.dataset.racloakV1WishlistToggle = key;
    wishlist.dataset.racloakV1WishlistProductId = option.dataset.productId || '';
    wishlist.dataset.racloakV1WishlistVariantId = option.dataset.productVariantId || '';
    wishlist.dataset.racloakV1WishlistTitle = title;
    wishlist.dataset.racloakV1LabelAdd = `Add ${title} to wishlist`;
    wishlist.dataset.racloakV1LabelRemove = `Remove ${title} from wishlist`;
    window.racloakV1SectionRuntime?.wishlist?.sync(card);
  };

  const selectColor = (card, option) => {
    const title = option.dataset.productTitle || '';
    const url = option.dataset.productUrl || option.href;
    card.dataset.racloakV1CurrentProductId = option.dataset.productId || '';

    card.querySelectorAll(colorSelector).forEach((swatch) => {
      const selected = swatch === option;
      swatch.classList.toggle('is-current', selected);
      if (selected) swatch.setAttribute('aria-current', 'page');
      else swatch.removeAttribute('aria-current');
    });

    updateLink(card.querySelector('.racloak-v1-product-card__media-link'), url, `View ${title}`);
    const titleLink = card.querySelector('[data-racloak-v1-product-card-title-link]');
    updateLink(titleLink, url);
    if (titleLink) titleLink.textContent = title;

    const subtitle = card.querySelector('[data-racloak-v1-product-card-subtitle]');
    if (subtitle) {
      const subtitleText = option.dataset.productSubtitle || '';
      subtitle.textContent = subtitleText;
      subtitle.classList.toggle('is-empty', !subtitleText);
      if (subtitleText) subtitle.removeAttribute('aria-hidden');
      else subtitle.setAttribute('aria-hidden', 'true');
    }

    updatePrice(card, option);
    updateImage(card, option);
    updateBadge(card, option);
    updateReviews(card, option);
    updateWishlist(card, option);

    const status = card.querySelector('[data-racloak-v1-product-card-status]');
    if (status) status.textContent = `${title} selected`;
  };

  document.addEventListener('click', (event) => {
    const option = event.target.closest?.(colorSelector);
    if (!option) return;
    const card = option.closest(cardSelector);
    if (!card) return;
    event.preventDefault();
    selectColor(card, option);
  });

  document.addEventListener('pointerover', (event) => {
    const mediaLink = event.target.closest?.('.racloak-v1-product-card__media-link');
    if (mediaLink) loadHoverImage(mediaLink.closest(cardSelector));
  });

  document.addEventListener('focusin', (event) => {
    const mediaLink = event.target.closest?.('.racloak-v1-product-card__media-link');
    if (mediaLink) loadHoverImage(mediaLink.closest(cardSelector));
  });

  window.racloakV1ProductCard = { selectColor, loadHoverImage };
})();
